import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, Avatar, Button, Tooltip, Skeleton, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/sk';
import 'dayjs/locale/cs';
import 'dayjs/locale/en';

import {
    DASH,
    inkSurface,
    PRESENCE_TONES,
    resolvePhotoUrl,
    getInitials,
} from './dashboardTokens';
import { Eyebrow } from './Surface';

const MAX_FACES = 8;

const LiveDot = ({ color }) => (
    <Box
        component="span"
        sx={{
            position: 'relative',
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: color,
            flexShrink: 0,
            '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                bgcolor: color,
                animation: 'effzonePulse 2.4s ease-out infinite',
            },
            '@keyframes effzonePulse': {
                '0%': { transform: 'scale(1)', opacity: 0.55 },
                '70%': { transform: 'scale(3.4)', opacity: 0 },
                '100%': { transform: 'scale(3.4)', opacity: 0 },
            },
            '@media (prefers-reduced-motion: reduce)': {
                '&::after': { animation: 'none' },
            },
        }}
    />
);

const LegendRow = ({ color, label, value, ink, hollow }) => (
    <Stack direction="row" alignItems="center" spacing={1}>
        <Box
            sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: hollow ? 'transparent' : color,
                border: hollow ? `1.5px solid ${ink.textFaint}` : 'none',
            }}
        />
        <Typography sx={{ fontSize: '0.8125rem', color: ink.textMuted }}>{label}</Typography>
        <Typography
            sx={{
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: ink.text,
                ...DASH.numeric,
            }}
        >
            {value}
        </Typography>
    </Stack>
);

const FacePile = ({ people, ink, surface }) => {
    const shown = people.slice(0, MAX_FACES);
    const rest = people.length - shown.length;

    return (
        <Stack direction="row" alignItems="center" sx={{ pl: 0.5 }}>
            {shown.map((p) => {
                const photo = resolvePhotoUrl(p.employeePhoto);
                return (
                    <Tooltip
                        key={p.employeeId}
                        title={`${p.employeeName}${p.since ? ` · od ${dayjs(p.since).format('H:mm')}` : ''}`}
                        arrow
                    >
                        <Avatar
                            component={RouterLink}
                            to={`/employees/${p.employeeId}`}
                            src={photo || undefined}
                            alt={p.employeeName}
                            sx={{
                                textDecoration: 'none',
                                transition: 'transform .15s ease',
                                '&:hover': { transform: 'translateY(-2px)' },
                                width: 34,
                                height: 34,
                                ml: '-6px',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                color: '#fff',
                                bgcolor: 'rgba(255,255,255,0.14)',
                                border: '2px solid',
                                borderColor: p.onBreak ? PRESENCE_TONES.onBreak : surface,
                                boxShadow: p.onBreak ? 'none' : `0 0 0 1px ${ink.hairline}`,
                            }}
                        >
                            {getInitials(p.employeeName)}
                        </Avatar>
                    </Tooltip>
                );
            })}
            {rest > 0 && (
                <Box
                    sx={{
                        ml: '-6px',
                        height: 34,
                        minWidth: 34,
                        px: 1,
                        borderRadius: '17px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255,255,255,0.12)',
                        border: `2px solid ${surface}`,
                        color: ink.text,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        ...DASH.numeric,
                    }}
                >
                    +{rest}
                </Box>
            )}
        </Stack>
    );
};

/**
 * Karta prítomnosti — jediná výrazná plocha prehľadu.
 *
 * Jedno veľké číslo (kto je teraz v práci), pod ním pás rozdelený na pracujúcich,
 * ľudí na prestávke a neprítomných, a tváre tých, ktorí sú práve na pracovisku.
 * Štyri pôvodné KPI dlaždice tu žijú ďalej, len prepojené do jedného tvrdenia.
 */
const PresenceHero = ({
    kpis,
    totalWorkplaces,
    byWorkplace,
    present,
    loading,
    t,
    language,
}) => {
    const theme = useTheme();
    const ink = inkSurface(theme.palette.mode === 'dark');
    const [barReady, setBarReady] = useState(false);

    const total = kpis.totalEmployees || 0;
    const atWorkplace = kpis.atWorkplace || 0;
    const onBreak = Math.min(kpis.onBreak || 0, atWorkplace);
    const working = Math.max(atWorkplace - onBreak, 0);
    const away = Math.max(total - atWorkplace, 0);

    const pct = (n) => (total > 0 ? (n / total) * 100 : 0);

    useEffect(() => {
        const id = window.requestAnimationFrame(() => setBarReady(true));
        return () => window.cancelAnimationFrame(id);
    }, []);

    const hasEmployees = total > 0;
    const nobodyIn = hasEmployees && atWorkplace === 0;

    const workplaceLine = (byWorkplace || [])
        .filter((w) => w.count > 0)
        .slice(0, 4)
        .map((w) => `${w.name} ${w.count}`)
        .join('  ·  ');

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: `${DASH.radius}px`,
                bgcolor: ink.bg,
                border: '1px solid',
                borderColor: ink.border,
                color: ink.text,
                p: { xs: 2.75, sm: 3.5 },
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                width: '100%',
                minHeight: { lg: DASH.heroMinHeight },
                // Jediný gradient na stránke — mäkké svetlo v rohu karty, aby
                // tmavá plocha nebola plochá čierna.
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -180,
                    right: -120,
                    width: 420,
                    height: 420,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(37,99,235,0.34) 0%, rgba(37,99,235,0) 68%)',
                    pointerEvents: 'none',
                },
            }}
        >
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: { xs: 2, sm: 3 } }}>
                    <LiveDot color={atWorkplace > 0 ? PRESENCE_TONES.working : ink.textFaint} />
                    <Eyebrow sx={{ color: ink.textMuted }}>{t('pages.dashboard.presence.now')}</Eyebrow>
                </Stack>

                {loading ? (
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="text" width={140} height={92} sx={{ bgcolor: 'rgba(255,255,255,0.09)' }} />
                        <Skeleton variant="text" width={220} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.09)' }} />
                    </Box>
                ) : (
                    <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
                        <Typography
                            component="p"
                            sx={{
                                fontFamily: DASH.display,
                                fontSize: 'clamp(3.75rem, 9vw, 5.5rem)',
                                fontWeight: 700,
                                letterSpacing: '-0.045em',
                                lineHeight: 0.92,
                                ...DASH.numeric,
                            }}
                        >
                            {atWorkplace}
                        </Typography>
                        <Typography sx={{ mt: 1.25, fontSize: '0.9375rem', color: ink.textMuted }}>
                            {hasEmployees
                                ? t('pages.dashboard.presence.ofTeam').replace('{total}', String(total))
                                : t('pages.dashboard.presence.noTeamYet')}
                        </Typography>
                    </Box>
                )}

                {/* Pás prítomnosti — pomery sú skutočné, nie dekoratívne */}
                <Box
                    aria-hidden
                    sx={{
                        display: 'flex',
                        gap: '3px',
                        height: 10,
                        borderRadius: '5px',
                        overflow: 'hidden',
                        bgcolor: ink.track,
                        ...(hasEmployees ? null : { border: `1px dashed ${ink.hairline}`, bgcolor: 'transparent' }),
                    }}
                >
                    {[
                        { key: 'working', value: working, color: PRESENCE_TONES.working },
                        { key: 'onBreak', value: onBreak, color: PRESENCE_TONES.onBreak },
                    ].filter((seg) => seg.value > 0).map((seg) => (
                        <Box
                            key={seg.key}
                            sx={{
                                width: barReady ? `${pct(seg.value)}%` : 0,
                                flexShrink: 0,
                                bgcolor: seg.color,
                                borderRadius: '5px',
                                transition: 'width .7s cubic-bezier(.22,.61,.36,1)',
                                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                            }}
                        />
                    ))}
                </Box>

                <Stack
                    direction="row"
                    spacing={2.5}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 1.75 }}
                >
                    <LegendRow
                        color={PRESENCE_TONES.working}
                        label={t('pages.dashboard.presence.working')}
                        value={working}
                        ink={ink}
                    />
                    <LegendRow
                        color={PRESENCE_TONES.onBreak}
                        label={t('pages.dashboard.presence.onBreak')}
                        value={onBreak}
                        ink={ink}
                    />
                    <LegendRow
                        hollow
                        label={t('pages.dashboard.presence.away')}
                        value={away}
                        ink={ink}
                    />
                </Stack>

                <Box sx={{ flex: 1, minHeight: 20 }} />

                {/* Tváre tých, čo sú práve na pracovisku */}
                {present.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <FacePile people={present} ink={ink} surface={ink.bg} />
                        {workplaceLine && (byWorkplace || []).length > 1 && (
                            <Typography sx={{ mt: 1.5, fontSize: '0.75rem', color: ink.textFaint }}>
                                {workplaceLine}
                            </Typography>
                        )}
                    </Box>
                )}

                {nobodyIn && !loading && (
                    <Typography sx={{ mt: 3, fontSize: '0.8125rem', color: ink.textFaint, maxWidth: 380 }}>
                        {t('pages.dashboard.presence.nobodyInHint')}
                    </Typography>
                )}

                {!hasEmployees && !loading && (
                    <Button
                        component={RouterLink}
                        to="/employees"
                        sx={{
                            mt: 3,
                            alignSelf: 'flex-start',
                            bgcolor: '#FFFFFF',
                            color: ink.bg,
                            fontWeight: 650,
                            px: 2.25,
                            height: 40,
                            borderRadius: '20px',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.88)' },
                        }}
                    >
                        {t('pages.dashboard.presence.addTeamCta')}
                    </Button>
                )}

                {/* Zvyšné dve dnešné čísla — potichu, na päte karty */}
                <Stack
                    direction="row"
                    spacing={4}
                    sx={{
                        mt: 3,
                        pt: 2.25,
                        borderTop: `1px solid ${ink.hairline}`,
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: '0.75rem', color: ink.textFaint, mb: 0.25 }}>
                            {t('pages.dashboard.kpis.workedToday')}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: DASH.display,
                                fontSize: '1.375rem',
                                fontWeight: 600,
                                letterSpacing: '-0.02em',
                                ...DASH.numeric,
                            }}
                        >
                            {kpis.workedToday || 0}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '0.75rem', color: ink.textFaint, mb: 0.25 }}>
                            {t('pages.dashboard.kpis.workplaces')}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: DASH.display,
                                fontSize: '1.375rem',
                                fontWeight: 600,
                                letterSpacing: '-0.02em',
                                ...DASH.numeric,
                            }}
                        >
                            {totalWorkplaces || 0}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Box sx={{ textAlign: 'right', alignSelf: 'flex-end' }}>
                        <Typography sx={{ fontSize: '0.75rem', color: ink.textFaint }}>
                            {dayjs().locale(language === 'CZ' ? 'cs' : language === 'EN' ? 'en' : 'sk').format('dddd D. MMMM')}
                        </Typography>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
};

export default PresenceHero;
